/* eslint-disable no-nested-ternary */
import { Grid, makeStyles, TextField } from '@material-ui/core';
import { Formik } from 'formik';
import i18n from 'i18n';
import React from 'react';
import { connect } from 'react-redux';
import GenericForm from 'skyone-lib/lib/GenericForm';
import Button from 'components/Button';

import {
  createDigitalCertificatesRequest,
  editDigitalCertificatesRequest,
} from 'modules/clients/actions';

import { getCertificateSchema } from './schema';
import styles from '../styles';

const useStyles = makeStyles(styles);

export type CertificateFormValues = {
  name: string;
  path: string;
  password: string;
};

const buildInitialValues = (
  initialName?: string,
  initialPath?: string
): CertificateFormValues => ({
  name: initialName || '',
  path: initialPath || '',
  password: '',
});

interface Props {
  mode: 'create' | 'edit';
  clientUuid: string;
  certificateUuid?: string;
  query: any;
  initialName?: string;
  initialPath?: string;
  createDigitalCertificatesRequest: (props) => void;
  editDigitalCertificatesRequest: (props) => void;
  onCancel: () => void;
  onCloseModal: () => void;
}

const FormCertificate = ({
  mode,
  clientUuid,
  certificateUuid,
  query,
  initialName,
  initialPath,
  createDigitalCertificatesRequest,
  editDigitalCertificatesRequest,
  onCancel,
  onCloseModal,
}: Props) => {
  const classes = useStyles();
  const [isEditingPassword, setIsEditingPassword] = React.useState(
    mode === 'create'
  );

  React.useEffect(() => {
    setIsEditingPassword(mode === 'create');
  }, [mode]);

  const handleSubmit = (values: CertificateFormValues) => {
    if (mode === 'create') {
      createDigitalCertificatesRequest({
        clientUuid,
        query,
        data: {
          name: values.name,
          path: values.path,
          password: values.password,
        },
      });
      onCloseModal();
      return;
    }

    const data: any = { path: values.path, name: values.name };
    if (isEditingPassword && values.password?.trim()) {
      data.password = values.password;
    }

    editDigitalCertificatesRequest({
      clientUuid,
      certificateUuid,
      query,
      data,
    });

    onCloseModal();
  };

  return (
    <Grid item container>
      <Formik
        initialValues={buildInitialValues(initialName, initialPath)}
        validationSchema={getCertificateSchema({ mode, isEditingPassword })}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ setFieldValue, setFieldTouched, submitForm, errors, values }) => (
          <Grid container spacing={2} className={classes.cloneClient}>
            <GenericForm
              fields={values}
              screenFields={[
                {
                  gridXs: 12,
                  label: i18n.t('NAME_KEY'),
                  stateKey: 'name',
                },
              ]}
              setFieldValue={setFieldValue}
              errors={errors}
            />
            <GenericForm
              fields={values}
              screenFields={[
                {
                  gridXs: 12,
                  label: i18n.t('DIGITAL_CERTIFICATE_PATH_KEY'),
                  stateKey: 'path',
                },
              ]}
              setFieldValue={setFieldValue}
              errors={errors}
            />

            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                id="certificate-password"
                label={i18n.t('PASSWORD_KEY')}
                type={
                  mode === 'edit'
                    ? isEditingPassword
                      ? 'text'
                      : 'password'
                    : 'text'
                }
                value={
                  mode === 'edit' && !isEditingPassword
                    ? '••••••••'
                    : values.password
                }
                onFocus={() => {
                  if (mode === 'edit' && !isEditingPassword) {
                    setIsEditingPassword(true);
                    setFieldValue('password', '');
                    setFieldTouched('password', false);
                  }
                }}
                onChange={e => {
                  if (mode === 'edit' && !isEditingPassword) return;
                  setFieldValue('password', e.target.value);
                }}
                onBlur={() => setFieldTouched('password', true)}
                error={!!errors.password}
                helperText={errors.password}
                inputProps={{
                  autoComplete: 'new-password',
                  spellCheck: false,
                }}
              />
            </Grid>

            <Grid container justify="flex-end" className={classes.buttonsBase}>
              <Grid container className={classes.buttonsAlign}>
                <Button id="submit-button" submitButton onClick={submitForm}>
                  {i18n.t('SAVE_KEY')}
                </Button>

                <Button
                  id="cancel-button"
                  variant="outlined"
                  onClick={onCancel}
                >
                  {i18n.t('CANCEL_KEY')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Formik>
    </Grid>
  );
};

export default connect(null, {
  createDigitalCertificatesRequest,
  editDigitalCertificatesRequest,
})(FormCertificate);
